import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Download, LogOut, RefreshCw, Users, Flag, AlertTriangle, BarChart3, FileText, Plus, Pencil, Trash2, X, MapPin, Flame, Sparkles, Eye, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import api, { API } from "../../lib/api";
import { fmtUSD } from "../../lib/format";

export default function AdminDashboard() {
  const { admin, ready, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ leads: [], flags: [], errors: [], analytics: null, reviews: [], cmaLeads: [], partialLeads: [], netSheetLeads: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (ready && !admin) nav("/admin/login"); }, [ready, admin, nav]);
  useEffect(() => { document.title = "Admin — Equitas Home Value"; }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [leads, flags, errors, analytics, reviews, cmaLeads, partialLeads, netSheetLeads] = await Promise.all([
        api.get("/admin/leads"), api.get("/admin/property-flags"), api.get("/admin/api-errors"),
        api.get("/admin/analytics"), api.get("/admin/broker-reviews"), api.get("/admin/cma-leads"),
        api.get("/admin/partial-leads"), api.get("/admin/net-sheet-leads"),
      ]);
      setData({
        leads: leads.data.leads, flags: flags.data.flags, errors: errors.data.errors,
        analytics: analytics.data, reviews: reviews.data.broker_reviews, cmaLeads: cmaLeads.data.cma_leads,
        partialLeads: partialLeads.data.partial_leads, netSheetLeads: netSheetLeads.data.net_sheet_leads,
      });
    } catch (e) { if (e.response?.status === 401) nav("/admin/login"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (admin) load(); }, [admin]);

  const token = () => localStorage.getItem("equitas_admin_token");
  const download = (path, name) => {
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      });
  };
  const regenerate = async (id) => { await api.post(`/admin/reports/${id}/regenerate`); toast.success("Report regenerated."); };

  if (!ready || (loading && !data.analytics)) return <div className="min-h-screen bg-brand-cream flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-emerald" /></div>;

  const a = data.analytics || { events: {}, lead_count: 0 };
  const stats = [
    [Users, "Report Requests", a.lead_count],
    [BarChart3, "Address Searches", a.events.address_search || 0],
    [FileText, "Valuations", a.events.valuation_completed || 0],
    [Flag, "Broker Reviews", a.events.broker_review_request || 0],
  ];

  return (
    <div className="min-h-screen bg-brand-cream paper-grain">
      <header className="border-b border-brand-hairline bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo /><span className="hidden sm:inline text-sm text-brand-stone">Admin Dashboard</span></div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="p-2 text-brand-stone hover:text-brand-emerald" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /></button>
            <span className="text-sm text-brand-stone hidden sm:inline">{admin?.email}</span>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout"
              className="flex items-center gap-1.5 rounded-lg border border-brand-hairline px-3 py-1.5 text-sm hover:border-brand-emerald"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(([Icon, label, val]) => (
            <div key={label} className="rounded-xl bg-white border border-brand-hairline p-5">
              <Icon className="h-5 w-5 text-brand-gold" />
              <p className="mt-3 font-mono text-3xl font-bold text-brand-emerald">{val}</p>
              <p className="text-xs text-brand-stone uppercase tracking-wider">{label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads" className="mt-8">
          <TabsList className="bg-white border border-brand-hairline flex-wrap h-auto">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="netsheets" data-testid="tab-netsheets">Net Sheets</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity Feed</TabsTrigger>
            <TabsTrigger value="cma" data-testid="tab-cma">CMA Requests</TabsTrigger>
            <TabsTrigger value="partial" data-testid="tab-partial">Partial Leads</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="taxes" data-testid="tab-taxes">Transfer Taxes</TabsTrigger>
            <TabsTrigger value="escrow" data-testid="tab-escrow">Escrow</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Cost Settings</TabsTrigger>
            <TabsTrigger value="flags" data-testid="tab-flags">Flags</TabsTrigger>
            <TabsTrigger value="errors" data-testid="tab-errors">API Errors</TabsTrigger>
            <TabsTrigger value="mls" data-testid="tab-mls">MLS (Broker)</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-5">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-serif text-2xl text-brand-ink">Report Requests</h2>
              <button onClick={() => download("/admin/leads/export.csv", "equitas-leads.csv")} data-testid="admin-export-csv-button"
                className="flex items-center gap-2 rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream hover:bg-brand-emeraldHover"><Download className="h-4 w-4" /> Export CSV</button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-brand-hairline bg-white">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-brand-sand text-brand-stone text-xs uppercase tracking-wider">
                  <tr><Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Address</Th><Th>Estimate</Th><Th>Mktg</Th><Th>Report</Th></tr>
                </thead>
                <tbody>
                  {data.leads.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-brand-stone">No report requests yet.</td></tr>}
                  {data.leads.map((l) => (
                    <tr key={l.lead_id} className="border-t border-brand-hairline" data-testid="admin-lead-row">
                      <Td>{(l.created_at || "").slice(0, 10)}</Td><Td>{l.name}</Td><Td>{l.email}</Td><Td>{l.phone || "—"}</Td>
                      <Td className="max-w-[200px] truncate">{l.address}</Td><Td>{fmtUSD(l.estimate)}</Td>
                      <Td>{l.marketing_consent ? "Yes" : "No"}</Td>
                      <Td>
                        <div className="flex gap-2">
                          <button onClick={() => download(`/admin/reports/${l.lead_id}/download`, "report.pdf")} className="text-brand-emerald underline text-xs" data-testid="admin-open-report">Open</button>
                          <button onClick={() => regenerate(l.lead_id)} className="text-brand-stone underline text-xs" data-testid="admin-regenerate-report">Regenerate</button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.reviews.length > 0 && (
              <div className="mt-6">
                <h3 className="font-serif text-xl text-brand-ink mb-2">Broker-review requests</h3>
                <div className="rounded-xl border border-brand-hairline bg-white divide-y divide-brand-hairline">
                  {data.reviews.map((r, i) => <div key={i} className="p-3 text-sm flex justify-between"><span>{r.name} · {r.email}</span><span className="text-brand-stone">{r.address}</span></div>)}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="cma" className="mt-5">
            <h2 className="font-serif text-2xl text-brand-ink mb-3">CMA Consultation Requests</h2>
            <p className="text-sm text-brand-stone mb-3">In-person Comparative Market Analysis requests routed to Louis Fong.</p>
            <div className="overflow-x-auto rounded-xl border border-brand-hairline bg-white">
              <table className="w-full text-sm" data-testid="admin-cma-table">
                <thead className="bg-brand-sand text-brand-stone text-xs uppercase tracking-wider">
                  <tr><Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Timeline</Th><Th>Address</Th></tr>
                </thead>
                <tbody>
                  {data.cmaLeads.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-brand-stone">No CMA requests yet.</td></tr>}
                  {data.cmaLeads.map((c) => (
                    <tr key={c.lead_id} className="border-t border-brand-hairline" data-testid="admin-cma-row">
                      <Td>{(c.created_at || "").slice(0, 10)}</Td><Td>{c.name}</Td><Td>{c.email}</Td><Td>{c.phone || "—"}</Td>
                      <Td><span className="rounded-full bg-brand-emeraldLight text-brand-emerald px-2.5 py-0.5 text-xs font-semibold">{c.timeline}</span></Td>
                      <Td className="max-w-[200px] truncate">{c.address || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="partial" className="mt-5">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-serif text-2xl text-brand-ink">Partial Leads {data.partialLeads.length > 0 && <span className="text-sm text-brand-stone">({data.partialLeads.length})</span>}</h2>
              <button onClick={() => download("/admin/partial-leads/export.csv", "equitas-partial-leads.csv")} data-testid="admin-partial-export-csv"
                className="flex items-center gap-2 rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream hover:bg-brand-emeraldHover"><Download className="h-4 w-4" /> Export CSV</button>
            </div>
            <p className="text-sm text-brand-stone mb-3 flex items-start gap-2"><MapPin className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" /> Verified property addresses that were valued on the site but never submitted contact info. Postcard-ready for follow-up. Addresses already in Report Requests are excluded. <span className="inline-flex items-center gap-1 text-brand-ink font-medium"><Flame className="h-3.5 w-3.5 text-orange-500" /> Hot</span> = searched 3+ times — mail these first.</p>
            <div className="overflow-x-auto rounded-xl border border-brand-hairline bg-white">
              <table className="w-full text-sm" data-testid="admin-partial-table">
                <thead className="bg-brand-sand text-brand-stone text-xs uppercase tracking-wider">
                  <tr><Th>Priority</Th><Th>Address</Th><Th>AVM Estimate</Th><Th>Searches</Th><Th>First Seen</Th><Th>Last Seen</Th></tr>
                </thead>
                <tbody>
                  {data.partialLeads.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-brand-stone">No partial leads yet.</td></tr>}
                  {data.partialLeads.map((p, i) => {
                    const hot = p.searches >= 3;
                    const reportUrl = `/valuation?address=${encodeURIComponent(p.address)}`;
                    const estimate = (typeof p.estimate === "number" && p.estimate > 0)
                      ? `$${Math.round(p.estimate).toLocaleString("en-US")}` : null;
                    return (
                    <tr key={i} className={`border-t border-brand-hairline ${hot ? "bg-orange-50/70" : ""}`} data-testid="admin-partial-row">
                      <Td>{hot
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2.5 py-0.5 text-xs font-semibold" data-testid="admin-partial-hot"><Flame className="h-3 w-3" /> Hot</span>
                        : <span className="text-brand-stone/50 text-xs">—</span>}</Td>
                      <Td className="font-medium text-brand-ink">
                        <a href={reportUrl} target="_blank" rel="noreferrer" data-testid="admin-partial-report-link"
                          className="inline-flex items-center gap-1.5 text-brand-emerald hover:underline">
                          {p.address} <ExternalLink className="h-3.5 w-3.5 shrink-0 text-brand-stone/60" />
                        </a>
                      </Td>
                      <Td className="font-semibold text-brand-ink" data-testid="admin-partial-avm">{estimate || <span className="text-brand-stone/50">—</span>}</Td>
                      <Td className={hot ? "font-bold text-orange-700" : ""}>{p.searches}</Td>
                      <Td>{(p.first_seen || "").slice(0, 10)}</Td>
                      <Td>{(p.last_seen || "").slice(0, 10)}</Td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="blog" className="mt-5"><BlogManager /></TabsContent>
          <TabsContent value="activity" className="mt-5"><ActivityFeed /></TabsContent>

          <TabsContent value="netsheets" className="mt-5">
            <h2 className="font-serif text-2xl text-brand-ink mb-3">Net-Sheet PDF Leads {data.netSheetLeads.length > 0 && <span className="text-sm text-brand-stone">({data.netSheetLeads.length})</span>}</h2>
            <p className="text-sm text-brand-stone mb-3">High-intent sellers who generated a net sheet on the Selling Costs calculator. Each receives an instant auto-reply with their PDF and a link to book Louis. Re-download any seller's exact sheet below.</p>
            <div className="overflow-x-auto rounded-xl border border-brand-hairline bg-white">
              <table className="w-full text-sm" data-testid="admin-netsheet-table">
                <thead className="bg-brand-sand text-brand-stone text-xs uppercase tracking-wider">
                  <tr><Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>City</Th><Th>Price</Th><Th>Equity Saved</Th><Th>Mktg</Th><Th>Sheet</Th></tr>
                </thead>
                <tbody>
                  {data.netSheetLeads.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-brand-stone">No net-sheet leads yet.</td></tr>}
                  {data.netSheetLeads.map((l) => (
                    <tr key={l.lead_id} className="border-t border-brand-hairline" data-testid="admin-netsheet-row">
                      <Td>{(l.created_at || "").slice(0, 10)}</Td><Td>{l.name}</Td><Td>{l.email}</Td><Td>{l.phone || "—"}</Td>
                      <Td>{l.city}{l.county ? `, ${l.county}` : ""}</Td><Td>{fmtUSD(l.estimate)}</Td>
                      <Td className="text-brand-emerald font-semibold">{l.saved ? fmtUSD(l.saved) : "—"}</Td>
                      <Td>{l.marketing_consent ? "Yes" : "No"}</Td>
                      <Td>
                        <button onClick={() => download(`/admin/reports/${l.lead_id}/download`, "equitas-net-sheet.pdf")} className="inline-flex items-center gap-1 text-brand-emerald underline text-xs" data-testid="admin-netsheet-download"><Download className="h-3.5 w-3.5" /> Download</button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="taxes" className="mt-5"><TaxEditor /></TabsContent>
          <TabsContent value="escrow" className="mt-5"><EscrowEditor /></TabsContent>
          <TabsContent value="settings" className="mt-5"><SettingsEditor /></TabsContent>

          <TabsContent value="flags" className="mt-5">
            <h2 className="font-serif text-2xl text-brand-ink mb-3">Property-data flags</h2>
            <div className="rounded-xl border border-brand-hairline bg-white divide-y divide-brand-hairline" data-testid="admin-flags-list">
              {data.flags.length === 0 && <p className="p-6 text-center text-brand-stone">No flags.</p>}
              {data.flags.map((f, i) => (
                <div key={i} className="p-4 text-sm"><p className="font-medium">{f.address}</p><p className="text-brand-stone mt-1">{f.corrections?.note}</p><p className="text-xs text-brand-stone/60 mt-1">{(f.created_at || "").slice(0, 10)}</p></div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="errors" className="mt-5">
            <h2 className="font-serif text-2xl text-brand-ink mb-3 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-brand-gold" /> Failed API requests</h2>
            <div className="rounded-xl border border-brand-hairline bg-white divide-y divide-brand-hairline" data-testid="admin-errors-list">
              {data.errors.length === 0 && <p className="p-6 text-center text-brand-stone">No errors logged.</p>}
              {data.errors.map((e, i) => (
                <div key={i} className="p-4 text-sm"><span className="font-mono text-xs bg-brand-sand px-2 py-0.5 rounded">{e.context}</span><p className="text-brand-stone mt-1">{e.message}</p><p className="text-xs text-brand-stone/60 mt-1">{(e.created_at || "").slice(0, 19).replace("T", " ")}</p></div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="mls" className="mt-5"><MlsBroker /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "", ...rest }) => <td className={`px-4 py-3 ${className}`} {...rest}>{children}</td>;

function MlsBroker() {
  const [configured, setConfigured] = useState(null);
  const [lat, setLat] = useState("37.3382");
  const [lng, setLng] = useState("-121.8863");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/admin/mls/status").then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false)); }, []);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/mls/comps", { params: { lat, lng, radius: 1 } }); setRes(data); }
    catch { toast.error("MLS request failed."); } finally { setLoading(false); }
  };
  const inp = "mt-1 block rounded-lg border border-brand-hairline px-3 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-brand-emerald";
  return (
    <div className="max-w-2xl">
      <h2 className="font-serif text-2xl text-brand-ink mb-2">MLS closed comps (broker only)</h2>
      <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${configured ? "bg-brand-emeraldLight text-brand-emerald" : "bg-brand-goldMuted text-brand-ink"}`} data-testid="mls-status">
        {configured === null ? "Checking…" : configured ? "MLS feed configured — closed comps available to the broker only." : "MLS feed not configured. Add MLSListings RESO Web API credentials (MLS_BASE_URL, MLS_TOKEN_URL, MLS_CLIENT_ID, MLS_CLIENT_SECRET) once your broker/VOW data license is approved. Public estimates use RentCast; MLS stays broker-only per license."}
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <div><label className="text-xs text-brand-stone">Latitude</label><input className={inp} value={lat} onChange={(e) => setLat(e.target.value)} data-testid="mls-lat" /></div>
        <div><label className="text-xs text-brand-stone">Longitude</label><input className={inp} value={lng} onChange={(e) => setLng(e.target.value)} data-testid="mls-lng" /></div>
        <button onClick={run} disabled={loading} data-testid="mls-fetch" className="rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream hover:bg-brand-emeraldHover disabled:opacity-60">{loading ? "Fetching…" : "Fetch comps within 1 mile"}</button>
      </div>
      {res && (
        <div className="mt-4 text-sm" data-testid="mls-results">
          {res.configured === false && <p className="text-brand-stone">{res.message}</p>}
          {res.error && <p className="text-destructive">{res.error}</p>}
          {res.comps && <p className="text-brand-stone mb-2">{res.count} closed comps · {res.attribution}</p>}
          {res.comps && res.comps.map((c, i) => (
            <div key={i} className="border-b border-brand-hairline py-2 flex justify-between gap-3">
              <span>{c.address}</span><span className="font-mono">{c.sale_price ? fmtUSD(c.sale_price) : "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaxEditor() {
  const [rules, setRules] = useState(null);
  useEffect(() => { api.get("/admin/tax-rules").then((r) => setRules(r.data.tax_rules)); }, []);
  const save = async () => {
    try { await api.put("/admin/tax-rules", { tax_rules: rules }); toast.success("Tax schedule saved."); }
    catch { toast.error("Invalid JSON or save failed."); }
  };
  if (!rules) return <Loader2 className="h-6 w-6 animate-spin text-brand-emerald" />;
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-serif text-2xl text-brand-ink">Transfer-tax schedules</h2>
        <button onClick={save} data-testid="save-tax-rules" className="rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream">Save</button>
      </div>
      <p className="text-sm text-brand-stone mb-3">Edit jurisdiction rules, rates, sources, effective dates, and seller allocation. Flagged (unverified) rules must be confirmed before production use.</p>
      <textarea data-testid="tax-rules-json" value={JSON.stringify(rules, null, 2)} onChange={(e) => { try { setRules(JSON.parse(e.target.value)); } catch { /* keep typing */ } }}
        className="w-full h-[520px] rounded-xl border border-brand-hairline bg-white p-4 font-mono text-xs outline-none focus:ring-1 focus:ring-brand-emerald" />
    </div>
  );
}

function EscrowEditor() {
  const [sched, setSched] = useState(null);
  useEffect(() => { api.get("/admin/escrow-schedule").then((r) => setSched(r.data.escrow_schedule)); }, []);
  const save = async () => {
    try { await api.put("/admin/escrow-schedule", { escrow_schedule: sched }); toast.success("Escrow schedule saved."); }
    catch { toast.error("Save failed."); }
  };
  if (!sched) return <Loader2 className="h-6 w-6 animate-spin text-brand-emerald" />;
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-serif text-2xl text-brand-ink">Escrow fee schedule</h2>
        <button onClick={save} data-testid="save-escrow" className="rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream">Save</button>
      </div>
      <p className="text-sm text-brand-stone mb-3">Replace with your preferred escrow company's current schedule. Store provider, source document, effective date, and brackets.</p>
      <textarea data-testid="escrow-json" value={JSON.stringify(sched, null, 2)} onChange={(e) => { try { setSched(JSON.parse(e.target.value)); } catch {} }}
        className="w-full h-[520px] rounded-xl border border-brand-hairline bg-white p-4 font-mono text-xs outline-none focus:ring-1 focus:ring-brand-emerald" />
    </div>
  );
}

function SettingsEditor() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/cost-settings").then((r) => setS(r.data.cost_settings)); }, []);
  const save = async () => { await api.put("/admin/cost-settings", { cost_settings: s }); toast.success("Settings saved."); };
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-brand-emerald" />;
  const num = (k, label) => (
    <div><label className="text-xs text-brand-stone">{label}</label>
      <input data-testid={`setting-${k}`} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) || 0 })}
        className="mt-1 w-full rounded-lg border border-brand-hairline px-3 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-brand-emerald" /></div>
  );
  return (
    <div className="max-w-lg">
      <h2 className="font-serif text-2xl text-brand-ink mb-4">Default cost assumptions</h2>
      <div className="grid grid-cols-2 gap-4">
        {num("equitas_listing_fee_pct", "Equitas listing fee %")}
        {num("dual_combined_pct", "Dual combined %")}
        {num("buyer_side_default_pct", "Buyer-side default %")}
        {num("title_insurance_default", "Title insurance default $")}
        {num("hoa_transfer_default", "HOA transfer default $")}
      </div>
      <button onClick={save} data-testid="save-settings" className="mt-5 rounded-lg bg-brand-emerald px-5 py-2.5 text-sm text-brand-cream">Save Settings</button>
    </div>
  );
}


const EMPTY_POST = { title: "", slug: "", category: "", meta_description: "", featured_image: "", body_html: "", author: "Louis Fong" };

const ACTIVITY_LABELS = {
  address_search: "Address search",
  slider_change: "Net-sheet slider",
  net_sheet_customize_click: "Customize net sheet",
  contact_click: "Contact click",
};

function activityDetail(e) {
  const m = e.meta || {};
  if (e.type === "address_search") return m.address || "—";
  if (e.type === "slider_change") return `${m.field || "field"} → ${m.value}`;
  if (e.type === "contact_click") return m.channel || "—";
  if (e.type === "net_sheet_customize_click") return m.net ? fmtUSD(m.net) : "opened Step 5";
  return "—";
}

function ActivityFeed() {
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const fetchFeed = async () => {
    try { const { data } = await api.get("/admin/activity?limit=150"); setEvents(data.events || []); } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchFeed(); }, []);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(fetchFeed, 5000);
    return () => clearInterval(id);
  }, [live]);

  return (
    <div data-testid="activity-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-serif text-xl text-brand-ink">User Activity Feed</h2>
          <span className="text-sm text-brand-stone">({events.length})</span>
          {live && <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> live</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setLive((v) => !v)} data-testid="activity-live-toggle"
            className="rounded-lg border border-brand-hairline bg-white px-3 py-1.5 text-xs font-medium text-brand-ink hover:bg-brand-cream">
            {live ? "Pause" : "Resume"} auto-refresh
          </button>
          <button onClick={fetchFeed} data-testid="activity-refresh" className="rounded-lg border border-brand-hairline bg-white p-2 text-brand-ink hover:bg-brand-cream"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-brand-hairline bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-cream/60 text-brand-stone"><tr><Th>Time</Th><Th>Event</Th><Th>Detail</Th><Th>City</Th><Th>IP</Th></tr></thead>
          <tbody className="divide-y divide-brand-hairline">
            {loading && events.length === 0 && <tr><Td className="text-brand-stone" >Loading…</Td></tr>}
            {!loading && events.length === 0 && <tr><Td className="text-brand-stone">No activity yet.</Td></tr>}
            {events.map((e) => (
              <tr key={e.id} data-testid="activity-row">
                <Td className="whitespace-nowrap text-brand-stone">{new Date(e.created_at).toLocaleString()}</Td>
                <Td className="whitespace-nowrap font-medium text-brand-ink">{ACTIVITY_LABELS[e.type] || e.type}</Td>
                <Td className="max-w-[260px] truncate" title={activityDetail(e)}>{activityDetail(e)}</Td>
                <Td className="whitespace-nowrap">{e.city || "—"}</Td>
                <Td className="whitespace-nowrap font-mono text-xs text-brand-stone">{e.ip || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState(null);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | slug
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [autoPublish, setAutoPublish] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pillar, setPillar] = useState("local");
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState("");

  const load = async () => {
    const [list, settings, ar] = await Promise.all([api.get("/admin/blog"), api.get("/admin/blog/settings"), api.get("/admin/blog/areas")]);
    setPosts(list.data.posts);
    setCats([...new Set(list.data.posts.map((p) => p.category).filter(Boolean))].sort());
    setAutoPublish(!!settings.data.auto_publish);
    setAreas(ar.data.areas || []);
  };
  useEffect(() => { load(); }, []);

  const generateNow = async (p) => {
    setGenerating(p || "local");
    try {
      const body = { pillar: p || "local" };
      if (area) body.area = area;
      const { data } = await api.post("/admin/blog/generate", body);
      toast.success(`Draft generated: ${data.post.title}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed."); }
    finally { setGenerating(false); }
  };

  const toggleAuto = async () => {
    const next = !autoPublish;
    setAutoPublish(next);
    try { await api.put("/admin/blog/settings", { auto_publish: next }); toast.success(`Auto-publish ${next ? "on" : "off"}.`); }
    catch { setAutoPublish(!next); toast.error("Could not update setting."); }
  };

  const publish = async (slug) => {
    try { await api.post(`/admin/blog/${slug}/publish`); toast.success("Post published."); await load(); }
    catch { toast.error("Publish failed."); }
  };

  const openNew = () => { setForm(EMPTY_POST); setEditing("new"); };
  const openEdit = async (slug) => {
    try {
      const { data } = await api.get(`/blog/${slug}`);
      setForm({ ...EMPTY_POST, ...data });
      setEditing(slug);
    } catch { toast.error("Could not load post."); }
  };
  const close = () => { setEditing(null); setForm(EMPTY_POST); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/blog", form);
      else await api.put(`/admin/blog/${editing}`, form);
      toast.success("Post saved.");
      close(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const del = async (slug, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blog/${slug}`); toast.success("Post deleted."); await load(); }
    catch { toast.error("Delete failed."); }
  };

  const inp = "mt-1 w-full rounded-lg border border-brand-hairline px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand-emerald";
  const filtered = (posts || []).filter((p) => !q || (p.title || "").toLowerCase().includes(q.toLowerCase()));

  if (editing !== null) {
    return (
      <div className="max-w-3xl" data-testid="blog-editor">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-serif text-2xl text-brand-ink">{editing === "new" ? "New post" : "Edit post"}</h2>
          <button onClick={close} data-testid="blog-editor-close" className="flex items-center gap-1.5 text-sm text-brand-stone hover:text-brand-emerald"><X className="h-4 w-4" /> Cancel</button>
        </div>
        <div className="space-y-4 rounded-xl border border-brand-hairline bg-white p-6">
          <div><label className="text-xs text-brand-stone">Title *</label>
            <input data-testid="blog-form-title" className={inp} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-brand-stone">Slug {editing === "new" && "(auto from title if blank)"}</label>
              <input data-testid="blog-form-slug" className={inp} value={form.slug || ""} disabled={editing !== "new"} placeholder="url-friendly-slug"
                onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              {editing !== "new" && <p className="mt-1 text-[0.7rem] text-brand-stone/60">Slug is fixed after creation to preserve SEO.</p>}</div>
            <div><label className="text-xs text-brand-stone">Category</label>
              <input data-testid="blog-form-category" className={inp} list="blog-cats" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <datalist id="blog-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist></div>
          </div>
          <div><label className="text-xs text-brand-stone">Meta description (SEO)</label>
            <input data-testid="blog-form-meta" className={inp} maxLength={200} value={form.meta_description || ""} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} /></div>
          <div><label className="text-xs text-brand-stone">Featured image URL</label>
            <input data-testid="blog-form-image" className={inp} value={form.featured_image || ""} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} /></div>
          <div><label className="text-xs text-brand-stone">Body (HTML)</label>
            <textarea data-testid="blog-form-body" value={form.body_html || ""} onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              className="mt-1 w-full h-[360px] rounded-lg border border-brand-hairline px-3 py-2 font-mono text-xs outline-none focus:ring-1 focus:ring-brand-emerald" placeholder="<p>Your article HTML…</p>" /></div>
          <button onClick={save} disabled={saving} data-testid="blog-form-save" className="rounded-lg bg-brand-emerald px-5 py-2.5 text-sm text-brand-cream hover:bg-brand-emeraldHover disabled:opacity-60">
            {saving ? "Saving…" : "Save post"}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-hairline bg-brand-emeraldLight/40 p-4" data-testid="blog-ai-panel">
        <div className="flex items-start gap-2.5">
          <Sparkles className="h-5 w-5 text-brand-gold shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-brand-ink">Automated daily blog</p>
            <p className="text-xs text-brand-stone">A new Santa Clara County post is generated daily at 6:00 AM PST (agent-comparison Mon, net-proceeds Wed, 2% Direct Sale Thu, local otherwise). With auto-publish on, every daily post goes live automatically; turn it off to save them as drafts for review instead.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleAuto} data-testid="blog-autopublish-toggle" role="switch" aria-checked={autoPublish}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoPublish ? "bg-brand-emerald" : "bg-brand-hairline"}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoPublish ? "translate-x-6" : "translate-x-1"}`} />
          </button>
          <span className="text-xs font-medium text-brand-ink">Auto-publish daily posts</span>
          <select value={pillar} onChange={(e) => setPillar(e.target.value)} disabled={!!generating} data-testid="blog-pillar-select"
            className="rounded-lg border border-brand-ink/20 bg-white px-3 py-2 text-sm text-brand-ink outline-none focus:ring-1 focus:ring-brand-emerald disabled:opacity-60">
            <option value="local">Local (Santa Clara County)</option>
            <option value="agent_comparison">Agent Comparison</option>
            <option value="direct_sale">2% Direct Sale</option>
            <option value="net_proceeds">Net Proceeds</option>
            <option value="neighborhood_guide">Neighborhood Home Guide</option>
          </select>
          <select value={area} onChange={(e) => setArea(e.target.value)} disabled={!!generating} data-testid="blog-area-select"
            className="max-w-[220px] rounded-lg border border-brand-ink/20 bg-white px-3 py-2 text-sm text-brand-ink outline-none focus:ring-1 focus:ring-brand-emerald disabled:opacity-60">
            <option value="">Auto / Next in Rotation</option>
            {areas.map((g) => (
              <optgroup key={g.city} label={g.city}>
                {g.areas.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </optgroup>
            ))}
          </select>
          <button onClick={() => generateNow(pillar)} disabled={!!generating} data-testid="blog-generate-now"
            className="flex items-center gap-1.5 rounded-lg bg-brand-ink px-4 py-2 text-sm text-brand-cream hover:opacity-90 disabled:opacity-60 whitespace-nowrap">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? "Generating…" : "Generate draft"}</button>
        </div>
      </div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="font-serif text-2xl text-brand-ink">Blog posts {posts && <span className="text-sm text-brand-stone">({posts.length})</span>}</h2>
        <div className="flex gap-2">
          <input data-testid="blog-admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles…"
            className="rounded-lg border border-brand-hairline px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand-emerald" />
          <button onClick={openNew} data-testid="blog-new-button" className="flex items-center gap-1.5 rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream hover:bg-brand-emeraldHover whitespace-nowrap"><Plus className="h-4 w-4" /> New post</button>
        </div>
      </div>
      {!posts && <Loader2 className="h-6 w-6 animate-spin text-brand-emerald" />}
      {posts && (
        <div className="overflow-x-auto rounded-xl border border-brand-hairline bg-white">
          <table className="w-full text-sm" data-testid="blog-admin-table">
            <thead className="bg-brand-sand text-brand-stone text-xs uppercase tracking-wider">
              <tr><Th>Title</Th><Th>Location</Th><Th>Status</Th><Th>Date</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-brand-stone">No posts.</td></tr>}
              {filtered.map((p) => {
                const isDraft = p.status === "draft";
                return (
                <tr key={p.slug} className="border-t border-brand-hairline" data-testid="blog-admin-row">
                  <Td className="max-w-[360px]"><span className="font-medium text-brand-ink">{p.title}</span><br /><span className="text-xs text-brand-stone/60">/{p.slug}</span></Td>
                  <Td className="text-xs text-brand-stone">{p.location || "—"}</Td>
                  <Td>{isDraft
                    ? <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-semibold" data-testid="blog-status-draft">Draft</span>
                    : <span className="rounded-full bg-brand-emeraldLight text-brand-emerald px-2.5 py-0.5 text-xs font-semibold" data-testid="blog-status-published">Published</span>}</Td>
                  <Td className="text-xs text-brand-stone">{(p.published_date || p.created_at || "").slice(0, 10)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-3">
                      {isDraft && <button onClick={() => publish(p.slug)} data-testid="blog-publish-button" className="inline-flex items-center gap-1 text-brand-emerald text-xs font-semibold underline"><Send className="h-3.5 w-3.5" /> Publish</button>}
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid="blog-preview-button" className="inline-flex items-center gap-1 text-brand-stone text-xs underline"><Eye className="h-3.5 w-3.5" /> Preview</a>
                      <button onClick={() => openEdit(p.slug)} data-testid="blog-edit-button" className="inline-flex items-center gap-1 text-brand-emerald text-xs underline"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                      <button onClick={() => del(p.slug, p.title)} data-testid="blog-delete-button" className="inline-flex items-center gap-1 text-destructive text-xs underline"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                    </div>
                  </Td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
